import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { computeMonthly } from "@/lib/lease";
import { usePartner } from "@/context/PartnerContext";
import { productName } from "@/lib/branding";
import Icon from "@/components/Icon";
import { ArrowRight, ShieldCheck, Zap, Boxes, Cpu } from "lucide-react";
import { tr } from "@/lib/i18n";

export default function HomePage() {
  const t = useT();
  const { lang, formatPrice, leaseSettings } = useApp();
  const { partner, prefix, isPartner } = usePartner();
  const nav = useNavigate();
  const [sectors, setSectors] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/sectors").then((r) => setSectors(r.data)),
      api.get("/bundles").then((r) => setBundles(r.data)),
      api.get("/hardware", { params: { featured: true } }).then((r) => setFeatured(r.data)),
    ]).catch(() => {});
  }, []);

  // Filter sectors if partner restricts them
  const visibleSectors = partner?.allowed_sector_slugs?.length
    ? sectors.filter((s) => partner.allowed_sector_slugs.includes(s.slug))
    : sectors;

  // Partner-aware hero copy (falls back to i18n defaults)
  const heroTitle = partner?.hero_title_nl || t.home.hero_title;
  const heroSub = partner?.hero_subtitle_nl || t.home.hero_sub;
  const heroCta = partner?.hero_cta_nl || t.home.cta_start;
  const heroTagline = partner?.tagline_nl || t.tagline;
  const primary = partner?.primary_color || "#6FAA8A";
  const accent = partner?.accent_color || "#D97742";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="grain-bg overflow-hidden relative">
        <div className="container-x grid md:grid-cols-12 gap-10 py-20 md:py-28 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="md:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
                 style={{ background: `${primary}1A`, color: primary }}
                 data-testid="hero-badge">
              {heroTagline}
            </div>
            <h1 className="display font-semibold text-[#F4F1EA] text-[2.6rem] sm:text-5xl lg:text-[4.2rem] leading-[1.02] tracking-tight">
              {isPartner ? (
                heroTitle
              ) : (
                <>
                  {heroTitle.split(" ").slice(0, -2).join(" ")}{" "}
                  <span className="text-[#6FAA8A] relative">
                    {heroTitle.split(" ").slice(-2).join(" ")}
                    <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10">
                      <path d="M2 7 C 80 2, 180 12, 298 4" stroke="#D97742" strokeWidth="3" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-[#A8B3AD] leading-relaxed max-w-xl">{heroSub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => nav(`${prefix}/configurator`)} className="btn-accent" data-testid="start-configurator-button"
                      style={isPartner ? { background: accent, color: "#fff" } : undefined}>
                <Zap className="w-4 h-4" /> {heroCta}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => nav(`${prefix}/catalog`)} className="btn-outline" data-testid="browse-catalog-button">
                {t.home.cta_catalog}
              </button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-[#A8B3AD] flex-wrap">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: primary }} /> Multi-vendor hardware</div>
              <div className="flex items-center gap-2"><Boxes className="w-4 h-4" style={{ color: primary }} /> Koop · Lease · Abonnement</div>
              {!isPartner && (
                <div className="flex items-center gap-2"><Cpu className="w-4 h-4 text-[#D97742]" /> Powered by QX Innovations</div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="md:col-span-5 relative"
          >
            <div className="absolute -top-10 -right-8 w-64 h-64 rounded-full blur-3xl" style={{ background: `${accent}1A` }} />
            <div className="absolute -bottom-12 -left-6 w-48 h-48 rounded-full blur-3xl" style={{ background: `${primary}26` }} />
            <div className="relative card-surface p-4 rounded-3xl shadow-xl" data-theme="dark">
              <img
                src={featured[0]?.image_url || "/images/qiox/clean/image3-25.png"}
                alt="Kiosk"
                className="rounded-2xl w-full h-[420px] object-contain bg-gradient-to-br from-[#1C2724] to-[#0F1613] p-4"
              />
              {featured[0] && (
                <div className="absolute bottom-8 left-8 backdrop-blur-md px-4 py-3 rounded-xl shadow-lg" style={{ background: "rgba(20, 28, 25, 0.95)", border: "1px solid #243430" }}>
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#A8B3AD" }}>Featured</div>
                  <div className="font-semibold text-sm" style={{ color: "#F4F1EA" }} data-testid="hero-featured-name">{productName(featured[0], partner)}</div>
                  <div className="text-xs font-semibold" style={{ color: accent }}>vanaf {formatPrice(computeMonthly(featured[0].purchase_price || 0, leaseSettings).monthly)}/mnd</div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="section" data-testid="sectors-section">
        <div className="container-x">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">01 · Start</div>
              <h2 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.home.pick_sector}</h2>
              <p className="mt-3 text-[#A8B3AD] max-w-xl">{t.home.pick_sector_sub}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleSectors.map((s, i) => (
              <motion.button
                key={s.slug}
                onClick={() => nav(`/configurator?sector=${s.slug}`)}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.5 }} viewport={{ once: true }}
                className="card-interactive text-left overflow-hidden group"
                data-testid={`sector-${s.slug}-card`}
              >
                <div className="h-48 product-plate relative overflow-hidden">
                  {s.image_url && (
                    <img src={s.image_url} alt={tr(s.name_i18n, lang)}
                      referrerPolicy="no-referrer" loading="lazy"
                      className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B110F] via-[#0B110F]/40 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-[#141C19]/90 backdrop-blur-md border border-[#6FAA8A]/30 grid place-items-center text-[#6FAA8A] shadow-xl shadow-black/40">
                    <Icon name={s.icon} className="w-5 h-5" />
                  </div>
                  <div className="absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#F4F1EA]/70">
                    0{s.order || 1}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="display text-xl font-semibold text-[#F4F1EA] tracking-tight">{tr(s.name_i18n, lang)}</h3>
                    <ArrowRight className="w-5 h-5 text-[#6A7672] group-hover:text-[#D97742] group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-sm text-[#A8B3AD] mt-2 leading-relaxed">{tr(s.description_i18n, lang)}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED HARDWARE */}
      {featured.length > 0 && (
        <section className="section pt-0" data-testid="featured-section">
          <div className="container-x">
            <div className="mb-8">
              <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">02 · Showcase</div>
              <h2 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">Meest gekozen kiosken</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((h, i) => (
                <motion.div
                  key={h.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }} viewport={{ once: true }}
                  className="card-interactive overflow-hidden"
                  onClick={() => nav(`/product/${h.id}`)}
                  data-testid={`featured-${h.id}-card`}
                >
                  <div className="bg-gradient-to-br from-[#1C2724] to-[#131B18] h-60 grid place-items-center p-6">
                    {h.image_url && <img src={h.image_url} alt={h.name} className="max-h-full max-w-full object-contain" />}
                  </div>
                  <div className="p-5">
                    <div className="chip chip-outline mb-2">{!isPartner ? h.vendor_slug : (h.type || "Kiosk")}</div>
                    <h4 className="font-semibold text-[#F4F1EA]">{productName(h, partner)}</h4>
                    <div className="mt-2 text-sm text-[#A8B3AD] capitalize">{h.placement} · {h.type}</div>
                    <div className="mt-3 flex items-baseline justify-between">
                      <div>
                        <div className="text-xs text-[#A8B3AD]">vanaf</div>
                        <div className="display text-lg font-semibold text-[#6FAA8A]">{formatPrice(computeMonthly(h.purchase_price || 0, leaseSettings).monthly)}/mnd</div>
                      </div>
                      <div className="text-xs text-[#A8B3AD]">
                        of koop {formatPrice(h.purchase_price)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BUNDLES */}
      {bundles.length > 0 && (
        <section className="section pt-0" data-testid="bundles-section">
          <div className="container-x">
            <div className="mb-8">
              <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">03 · Pakketten</div>
              <h2 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.home.bundles_title}</h2>
              <p className="mt-3 text-[#A8B3AD]">{t.home.bundles_sub}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bundles.slice(0, 3).map((b, i) => (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                  className="card-interactive p-6"
                  onClick={() => nav(`/bundles/${b.id}`)}
                  data-testid={`bundle-${b.slug}-card`}
                >
                  <div className="flex items-start justify-between">
                    <div className="chip chip-accent">{b.discount_pct}% voordeel</div>
                    <ArrowRight className="w-5 h-5 text-[#A8B3AD]" />
                  </div>
                  <h3 className="display text-xl font-semibold text-[#F4F1EA] mt-4">{tr(b.name_i18n, lang)}</h3>
                  <p className="text-sm text-[#A8B3AD] mt-2 leading-relaxed">{tr(b.description_i18n, lang)}</p>
                  {b.image_url && (
                    <div className="mt-5 rounded-xl bg-gradient-to-br from-[#1C2724] to-[#131B18] h-48 grid place-items-center p-4">
                      <img src={b.image_url} alt={tr(b.name_i18n, lang)} className="max-h-full object-contain" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="section" data-testid="how-section">
        <div className="container-x">
          <div className="mb-10">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">04 · Proces</div>
            <h2 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{t.home.how_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {t.home.steps.map((s, i) => (
              <div key={i} className="card-surface p-6">
                <div className="w-10 h-10 rounded-full bg-[#1F3A2E] text-[#FDFBF7] font-semibold grid place-items-center display">{i + 1}</div>
                <div className="mt-4 font-semibold text-[#F4F1EA]">{s.t}</div>
                <div className="text-sm text-[#A8B3AD] mt-1">{s.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button onClick={() => nav("/configurator")} className="btn-accent" data-testid="home-cta-configurator">
              {t.home.cta_start} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
